import { useCallback } from 'react';
import type { JSX } from 'react';

import { Translate } from '@change-corgi/core/react/i18n';
import { useTracking } from '@change-corgi/core/react/tracking';
import { ButtonLink } from '@change-corgi/design-system-legacy/components/actions';
import { Icon } from '@change-corgi/design-system-legacy/components/icon';
import { iconLock } from '@change-corgi/design-system-legacy/icons';
import { Box, Container, Flex } from '@change-corgi/design-system-legacy/layout';
import { Heading } from '@change-corgi/design-system-legacy/typography';

import { trackMovementAddPetitionsClick, trackMovementEditClick } from 'src/app/pages/movement/shared/api';
import { MOVEMENT_EVENT_VIEW_CONTEXT } from 'src/app/pages/movement/shared/constants';

type AdminToolbarProps = {
	movementId: string;
};

export function AdminToolbar({ movementId }: AdminToolbarProps): JSX.Element | null {
	const track = useTracking();

	const editMovementPetitionsUrl = `/admin/m/${movementId}`;

	const addPetitionsUrl = '/admin/movement_petitions/new';

	const handleEditPetitionsClick = useCallback(() => {
		void trackMovementEditClick(movementId, MOVEMENT_EVENT_VIEW_CONTEXT, track);
	}, [movementId, track]);

	const handleAddPetitionsClick = useCallback(() => {
		void trackMovementAddPetitionsClick(movementId, MOVEMENT_EVENT_VIEW_CONTEXT, track);
	}, [movementId, track]);

	return (
		<Box sx={{ backgroundColor: 'primary-greyBackground' }}>
			<Container sx={{ height: 60 }} px={16}>
				<Flex sx={{ alignItems: 'center', height: '100%' }}>
					<Box data-testid="admin-title" sx={{ display: ['none', 'inline'] }}>
						<Icon icon={iconLock} mr={16} />
						<Heading as="span" size="h5">
							<Translate value="corgi.movement.show.admin_toolbar.title" />
						</Heading>
					</Box>
					<Box sx={{ flexGrow: [0, 1] }} />
					<Box>
						<ButtonLink
							variant="secondary"
							size="small"
							to={editMovementPetitionsUrl}
							data-testid="edit"
							data-qa="edit"
							mr={[0, 16]}
							onClick={handleEditPetitionsClick}
						>
							<Translate value="corgi.movement.show.admin_toolbar.edit_button" />
						</ButtonLink>
					</Box>
					<Box sx={{ flexGrow: [1, 0] }} />
					<Box>
						<ButtonLink
							variant="secondary"
							size="small"
							to={addPetitionsUrl}
							data-testid="add-petitions"
							data-qa="add-petitions"
							onClick={handleAddPetitionsClick}
						>
							<Translate value="corgi.movement.show.admin_toolbar.add_petitions_button" />
						</ButtonLink>
					</Box>
				</Flex>
			</Container>
		</Box>
	);
}
